import { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { X, ZoomIn } from 'lucide-react';

const photos = [
  { src: '/lovable-uploads/226ca07d-bbe9-41c3-85b9-1fee2fff5643.png', span: 'col-span-2 row-span-2' },
  { src: '/lovable-uploads/2a627e98-21cc-45ca-999a-d74a13f6b813.png', span: 'col-span-1 row-span-1' },
  { src: '/lovable-uploads/46a2abd0-374c-484d-8f2b-db9faefd5ffe.png', span: 'col-span-1 row-span-1' },
  { src: '/lovable-uploads/9cbec4b8-eb63-43e4-82a5-4e612fbff926.png', span: 'col-span-1 row-span-2' },
  { src: '/lovable-uploads/aaddff51-ad84-478d-ae31-df3861af57aa.png', span: 'col-span-2 row-span-1' },
  { src: '/lovable-uploads/b16fa090-be13-4f85-8efe-34cce7ec0315.png', span: 'col-span-1 row-span-1' },
  { src: '/lovable-uploads/c306018b-07c5-4d9a-bfb7-96e0bcf2f751.png', span: 'col-span-2 row-span-1' },
  { src: '/lovable-uploads/d893ea82-a6cd-4fa4-9514-e7260683107a.png', span: 'col-span-1 row-span-1' },
  { src: '/lovable-uploads/f36b1e04-d28c-42eb-964f-d714f0cd588c.png', span: 'col-span-1 row-span-1' },
];

/* ── Lightbox ── */
const Lightbox = ({ src, onClose }: { src: string; onClose: () => void }) => {
  const closeRef=useRef<HTMLButtonElement>(null);
  useEffect(()=>{
    const previous=document.activeElement as HTMLElement|null;
    const overflow=document.body.style.overflow;
    document.body.style.overflow='hidden';closeRef.current?.focus();
    const key=(event:KeyboardEvent)=>{if(event.key==='Escape')onClose();if(event.key==='Tab'){event.preventDefault();closeRef.current?.focus();}};
    document.addEventListener('keydown',key);
    return()=>{document.body.style.overflow=overflow;document.removeEventListener('keydown',key);previous?.focus();};
  },[onClose]);
  return <motion.div role="dialog" aria-modal="true" aria-label="School photo preview" className="fixed inset-0 z-[9990] flex items-center justify-center p-4 sm:p-8 bg-slate-950/90" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={onClose}>
    <img src={src} alt="Renaissance Garden Schools campus life" className="max-w-full max-h-[85dvh] object-contain rounded-xl shadow-2xl" onClick={e=>e.stopPropagation()}/>
    <button ref={closeRef} type="button" onClick={onClose} aria-label="Close photo preview" className="absolute top-4 right-4 h-12 w-12 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-lg"><X size={22}/></button>
  </motion.div>;
};

/* ── Single tile ── */
const Tile = ({
  photo,
  index,
  onOpen,
}: {
  photo: (typeof photos)[number];
  index: number;
  onOpen: (src: string) => void;
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px 0px' });

  return (
    <motion.button
      type="button" aria-label={`Open school photo ${index+1}`}
      ref={ref}
      initial={{ opacity: 0, scale: 0.97 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.55, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`${photo.span} group relative overflow-hidden rounded-2xl cursor-pointer bg-zinc-100`}
      onClick={() => onOpen(photo.src)}
    >
      <img
        src={photo.src}
        alt="Renaissance Garden Schools campus life"
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/40 transition-colors duration-300 flex items-center justify-center">
        <ZoomIn
          size={30}
          className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg"
        />
      </div>
    </motion.button>
  );
};

/* ── Heading ── */
const Heading = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px 0px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="text-center mb-14"
    >
      <p className="text-primary text-[11px] font-black tracking-[0.32em] uppercase mb-5">
        Campus Life
      </p>
      <h2 className="font-display font-black text-[clamp(2.5rem,6vw,4.5rem)] text-zinc-900 tracking-tighter leading-[0.9] mb-6">
        Life at <span className="text-gradient">Renaissance</span>
      </h2>
      <p className="text-zinc-500 text-lg font-medium max-w-xl mx-auto leading-relaxed">
        A glimpse into the vibrant, nurturing world our students call home.
      </p>
    </motion.div>
  );
};

const Gallery = () => {
  const [lightbox, setLightbox] = useState<string | null>(null);

  return (
    <section id="gallery" className="py-16 sm:py-20 lg:py-24 bg-white relative overflow-hidden">
      {/* soft ambient blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/4 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/4 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <Heading />

        {/* Bento grid — auto-rows keeps cells proportional */}
        <div className="grid grid-flow-dense grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[140px] sm:auto-rows-[200px] gap-3 sm:gap-4">
          {photos.map((photo, i) => (
            <Tile key={i} photo={photo} index={i} onOpen={setLightbox} />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
